import { BASE_URL } from '../baseUrl';

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

/** API / Elasticsearch may store `images` as a JSON string instead of an array. */
export function coerceImagesWireValue(images: unknown): unknown {
  if (typeof images !== 'string') {
    return images;
  }
  const t = images.trim();
  if (!t) {
    return [];
  }
  if (t.startsWith('[') || t.startsWith('{')) {
    try {
      return JSON.parse(t) as unknown;
    } catch {
      return images;
    }
  }
  if (
    /^https?:\/\//i.test(t) ||
    t.startsWith('/') ||
    t.startsWith('data:image/') ||
    stringLooksLikeImageFilename(t)
  ) {
    return [t];
  }
  return images;
}

const IMAGE_OBJECT_URL_KEY_NORMALIZED = new Set([
  'url',
  'src',
  'href',
  'path',
  'uri',
  'link',
  'imageurl',
  'imagepath',
  'filename',
  'name',
]);

function stringLooksLikeImageFilename(s: string): boolean {
  return /\.(jpe?g|png|gif|webp|svg|bmp|avif)(\?|#|$)/i.test(s.trim());
}

function imageUrlFromImageObject(item: Record<string, unknown>): string | undefined {
  for (const [k, v] of Object.entries(item)) {
    if (typeof v !== 'string' || !v.trim()) {
      continue;
    }
    const norm = k.toLowerCase().replace(/[_\s-]+/g, '');
    if (!IMAGE_OBJECT_URL_KEY_NORMALIZED.has(norm)) {
      continue;
    }
    const t = v.trim();
    if ((norm === 'name' || norm === 'filename') && !stringLooksLikeImageFilename(t)) {
      continue;
    }
    return t;
  }
  return undefined;
}

/**
 * Drop empty `images` / `Images` so thumbnail lookup can fall back to scalar keys
 * (booking/search rows often send `images: []` alongside `cover_image` / nested asset).
 */
export function prepareRecordForThumbnailLookup(
  record: Record<string, unknown>,
): Record<string, unknown> {
  const snap: Record<string, unknown> = { ...record };
  const wire = readRecordImagesWireValue(snap);
  if (wire !== undefined && wire !== null) {
    const coerced = coerceImagesWireValue(wire);
    if (Array.isArray(coerced) && coerced.length === 0) {
      delete snap.images;
      delete snap.Images;
    }
  }
  return snap;
}

/** `images` / `Images` on a GET or list row (undefined when the key is absent). */
export function readRecordImagesWireValue(record: Record<string, unknown>): unknown {
  if (Object.prototype.hasOwnProperty.call(record, 'images')) {
    return record.images;
  }
  if (Object.prototype.hasOwnProperty.call(record, 'Images')) {
    return record.Images;
  }
  return undefined;
}

/**
 * Paths from the record's `images` field only.
 * `[]` = explicitly empty; `null` = absent for display (caller may fall back to legacy keys).
 */
export function extractImagePathsFromRecordImagesField(
  record: Record<string, unknown>,
): string[] | null {
  const wire = readRecordImagesWireValue(record);
  if (wire === undefined || wire === null) {
    return null;
  }
  const coerced = coerceImagesWireValue(wire);
  if (Array.isArray(coerced) && coerced.length === 0) {
    return [];
  }
  const paths = extractImageUrlStrings(coerced);
  return paths.length > 0 ? paths : null;
}

export function extractImageUrlStrings(images: unknown): string[] {
  const coerced = coerceImagesWireValue(images);
  if (!Array.isArray(coerced)) {
    return [];
  }
  const out: string[] = [];
  for (const item of coerced) {
    if (typeof item === 'string' && item.trim()) {
      out.push(item.trim());
      continue;
    }
    if (isPlainObject(item)) {
      const uRaw = imageUrlFromImageObject(item);
      if (uRaw !== undefined) {
        out.push(uRaw);
      }
    }
  }
  return out;
}

/** Join API-relative image paths for `<img src>` (testable with an explicit base). */
export function joinAssetImageDisplayPath(apiBaseUrl: string, path: string): string {
  const base = apiBaseUrl.replace(/\/+$/, '');
  if (apiBaseUrl.startsWith('/')) {
    // CRA dev server proxies `/images/*` at the app root — not under `/dev-api-proxy`.
    if (path.startsWith('/images/')) {
      return path;
    }
    return `${base}${path}`;
  }
  if (
    typeof window !== 'undefined' &&
    process.env.NODE_ENV === 'production' &&
    process.env.REACT_APP_USE_SAME_ORIGIN === 'true' &&
    window.location?.origin
  ) {
    return `${window.location.origin.replace(/\/+$/, '')}${path}`;
  }
  return `${base}${path}`;
}

function assetImageJoinBase(path: string): string {
  return joinAssetImageDisplayPath(BASE_URL, path);
}

/**
 * Resolve image URL for `<img src>` — absolute URLs unchanged; server paths joined to API base.
 * Bare filenames (e.g. `image236.jpg`) are served under `/images/` on the Offizz API host.
 */
export function resolveAssetImageSrc(raw: string): string {
  const s = raw.trim();
  if (!s) {
    return '';
  }
  if (/^https?:\/\//i.test(s) || s.startsWith('data:') || s.startsWith('blob:')) {
    return s;
  }
  if (s.startsWith('/images/')) {
    return assetImageJoinBase(s);
  }
  if (s.startsWith('/')) {
    const base = BASE_URL.replace(/\/+$/, '');
    return `${base}${s}`;
  }
  if (stringLooksLikeImageFilename(s)) {
    return assetImageJoinBase(`/images/${s}`);
  }
  const base = BASE_URL.replace(/\/+$/, '');
  return `${base}/${s}`;
}

/** Whether a stored path string can load without a picked {@link File} preview. */
export function isLoadableImageWirePath(path: string): boolean {
  const t = path.trim();
  if (!t) {
    return false;
  }
  return (
    /^https?:\/\//i.test(t) ||
    t.startsWith('data:') ||
    t.startsWith('blob:') ||
    t.startsWith('/') ||
    stringLooksLikeImageFilename(t)
  );
}

export function wirePathToImageDisplaySrc(path: string | null | undefined): string | null {
  if (!path?.trim()) {
    return null;
  }
  if (!isLoadableImageWirePath(path)) {
    return null;
  }
  return resolveAssetImageSrc(path);
}
